import React from "react";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  CardImg,
  CardTitle,
  CardText,
} from "reactstrap";
import SimpleHeader from "@/components/Headers/SimpleHeader";
import TableWrapper from "@/utils/TableWrapper";
import type { TableWrapperProps } from "@/utils/TableWrapper";

/** The author side card: the fields the post list shows for a clicked byline. */
export type BlogAuthorCard = {
  name?: string;
  avatar?: string;
  role?: string;
  description?: string;
};

export type SitePostsViewProps = {
  posts: unknown[];
  columns: TableWrapperProps<unknown>["columns"];
  viewAuthors: boolean;
  author: BlogAuthorCard | null;
  onCloseAuthor: () => void;
};

/**
 * SCRUM-1148: the list renders through TableWrapper, so this view no longer
 * carries a search box, a pager or a hand-rolled `.slice()`. The container still
 * owns the role filter and the createdAt sort and hands the result in as `data`,
 * so newest-first stays the default order while the headers gain click-to-sort.
 *
 * Known consequence, recorded rather than discovered: the replaced input had
 * `placeholder="Search by post title or author name"` and
 * `aria-label="Search posts by title or author"`. TableWrapper's built-in box
 * carries neither. Fixing that belongs to TableWrapper, which SCRUM-1026 is
 * explicit about not modifying — ~13 other tables depend on it.
 */
function SitePostsView({
  posts,
  columns,
  viewAuthors,
  author,
  onCloseAuthor,
}: SitePostsViewProps) {
  return (
    <>
      <SimpleHeader name="Manage Posts" parentName="Global Tools" />

      <Container className="mt-2" fluid>
        <Row>
          <Col>
            <Card>
              <CardHeader>
                <h3 className="mb-0">Manage Posts</h3>
              </CardHeader>

              <CardBody>
                <TableWrapper data={posts} columns={columns} />
              </CardBody>
            </Card>
          </Col>

          {viewAuthors && (
            <Col lg="4">
              <Card>
                <CardImg
                  alt={author?.name || "Author"}
                  src={author?.avatar || ""}
                  top
                />
                <CardBody>
                  <CardTitle className="h2 mb-0">{author?.name}</CardTitle>
                  <small className="text-muted">{author?.role}</small>
                  <CardText className="mt-4">{author?.description}</CardText>
                  <Row>
                    <Col lg="6">
                      <div className="text-left">
                        <Button
                          className="px-0"
                          color="link"
                          type="button"
                          onClick={onCloseAuthor}
                          aria-label="Close author panel"
                        >
                          Close
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          )}
        </Row>
      </Container>
    </>
  );
}

export default SitePostsView;
